import PropTypes from 'prop-types';
import Icon from 'Components/Atoms/Icon/Icon';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from 'Utils/cn';
import OutlineArrowRight from 'Components/Atoms/OutlineIcons/OutlineArrowRight';

export default function ProviderConnectorsSelectorItem(props) {
    const {
        className,
        onClick,
        isBusiness,
        isRecommended,
        image,
        title,
        subtitle,
        syncProducts,
        expire,
        hasTransactionsBalance,
        isUnavailable,
        unavailableMessage,
        hasProblems,
    } = props;

    const { t } = useTranslation('providers');

    return (
        <button
            className={cn(
                'group inline-flex text-left items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-75 ring-primary/20 focus-visible:border-primary transition-all relative bg-white border border-gray-200 shadow-sm hover:bg-gray-50 text-dark-blue',
                'px-5 py-7 w-full gap-5 justify-between',
                isUnavailable && 'opacity-60 bg-gray-50',
                className,
            )}
            type="button"
            onClick={onClick}
            disabled={isUnavailable}
        >
            <div className="absolute top-2 right-2 flex gap-2">
                {isUnavailable && (
                    <span className="uppercase text-2xs tracking-widest rounded-sm px-2 py-1 bg-gray-200 font-semibold text-gray-600">
                        {t('connector-unavailable')}
                    </span>
                )}
                {isBusiness && (
                    <span className="uppercase text-2xs tracking-widest rounded-sm px-2 py-1 bg-primary font-semibold text-white">
                        {t('business')}
                    </span>
                )}
                {isRecommended && (
                    <span className="uppercase text-2xs tracking-widest rounded-sm px-2 py-1 bg-primary/20 font-semibold text-primary">
                        {t('recommended')}
                    </span>
                )}
            </div>
            <div className="shrink-0 mb-auto">
                <img src={image} className="w-12 h-auto block" alt={title} />
            </div>

            <div className="grow">
                <div className="text-lg font-semibold">{title}</div>
                <div className="grid gap-2">
                    <div className="text-gray-500 pb-2 text-xs">{subtitle}</div>
                    {isUnavailable && (
                        <div className="flex items-start gap-3 text-sm text-gray-600">
                            <Icon name="circle-alert" size={20} color="currentColor" className="shrink-0" />
                            <div>{unavailableMessage || t('connector-unavailable-description')}</div>
                        </div>
                    )}
                    {!isUnavailable && hasProblems && (
                        <div className="flex items-start gap-3 text-sm text-warning">
                            <Icon name="circle-alert" size={20} color="currentColor" className="shrink-0" />
                            <div>{unavailableMessage || t('connector-with-problems-description')}</div>
                        </div>
                    )}
                    {syncProducts && (
                        <div className="flex items-start gap-3 text-sm">
                            <Icon name="bank" size={20} color="currentColor" className="shrink-0" />
                            <div>{syncProducts}</div>
                        </div>
                    )}
                    {expire && (
                        <div className="flex items-start gap-3 text-sm">
                            <Icon name="time" size={20} color="currentColor" className="shrink-0" />
                            <div>{expire}</div>
                        </div>
                    )}
                    {hasTransactionsBalance && (
                        <div className="flex items-start gap-3 text-sm">
                            <Icon name="square-check" size={20} color="currentColor" className="shrink-0" />
                            <div>
                                <Trans t={t} i18nKey="transactions-with-balance">
                                    Transacciones
                                    <strong className="font-semibold">CON</strong> saldo
                                </Trans>
                            </div>
                        </div>
                    )}
                    {!hasTransactionsBalance && (
                        <div className="flex align-start gap-3 text-sm">
                            <Icon name="square-times" size={20} color="currentColor" className="shrink-0" />
                            <div>
                                <Trans t={t} i18nKey="transactions-without-balance">
                                    Transacciones
                                    <strong className="font-semibold">SIN</strong> saldo
                                </Trans>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {!isUnavailable && (
                <OutlineArrowRight className="shrink-0 text-dark-blue transition-transform duration-200 ease-in-out group-hover:translate-x-1" />
            )}
        </button>
    );
}

ProviderConnectorsSelectorItem.propTypes = {
    className: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    isBusiness: PropTypes.bool,
    isRecommended: PropTypes.bool,
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    syncProducts: PropTypes.string,
    expire: PropTypes.string,
    hasTransactionsBalance: PropTypes.bool,
    isUnavailable: PropTypes.bool,
    unavailableMessage: PropTypes.string,
    hasProblems: PropTypes.bool,
};
